/**
 * The one place in this app that touches the network.
 *
 * Every request is same-origin: the public VPS terminates TLS and reverse
 * proxies `/api/core/**` to core, so `connect-src 'self'` in the response CSP
 * means this bundle structurally cannot reach PayPal, eBay, a tax provider, or
 * any analytics origin even if something tried.
 *
 * `referrerPolicy: "no-referrer"` matters here more than on the operator
 * surfaces: an item page URL is not PII, but a checkout URL carries a quote id
 * and there is no reason to hand it to anything.
 */

import {
  CHECKOUT_TOKEN_HEADER,
  CORRELATION_HEADER,
  CSRF_HEADER
} from "./coreRoutes.js";
import type { CoreReply, CoreRequest, Transport } from "./transport.js";

const REQUEST_TIMEOUT_MS = 15_000;

export function createHttpTransport(origin = ""): Transport {
  return async function httpTransport(request: CoreRequest): Promise<CoreReply> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      [CORRELATION_HEADER]: request.correlationID
    };
    if (request.method === "POST") {
      headers["Content-Type"] = "application/json";
    }
    if (request.csrfToken !== undefined) {
      headers[CSRF_HEADER] = request.csrfToken;
    }
    if (request.checkoutToken !== undefined) {
      headers[CHECKOUT_TOKEN_HEADER] = request.checkoutToken;
    }

    const timeout = new AbortController();
    const timer = setTimeout(() => {
      timeout.abort();
    }, REQUEST_TIMEOUT_MS);
    request.signal?.addEventListener("abort", () => {
      timeout.abort();
    });

    try {
      const response = await fetch(`${origin}${request.path}`, {
        method: request.method,
        headers,
        // The session cookie is the only credential this surface can present,
        // and it only exists for a signed-in collector. A guest sends none.
        credentials: "same-origin",
        mode: "same-origin",
        redirect: "error",
        cache: "no-store",
        referrerPolicy: "no-referrer",
        signal: timeout.signal,
        ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) })
      });
      const text = await response.text();
      let body: unknown = null;
      if (text.length > 0) {
        try {
          body = JSON.parse(text);
        } catch {
          return { kind: "http", status: response.status, body: null };
        }
      }
      return { kind: "http", status: response.status, body };
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Request failed before a reply arrived.";
      return { kind: "network-error", detail };
    } finally {
      clearTimeout(timer);
    }
  };
}
